import React, { useState, useEffect } from "react";
import {
  Box,
  Flex,
  Select,
  Button,
  useColorModeValue,
  Icon,
  Text,
  Heading,
  Stack,
  Divider,
  Slider,
  SliderTrack,
  SliderFilledTrack,
  SliderThumb,
  Tooltip,
  HStack
} from "@chakra-ui/react";
import { FaFilter, FaMapMarkerAlt } from "react-icons/fa";
import { State, City } from "country-state-city";

const FilterBar = ({ onFilterChange, initialFilters = {}, showDistanceFilter = false }) => {
  const [filters, setFilters] = useState({
    state: initialFilters.state || '',
    district: initialFilters.district || '',
    radius: initialFilters.radius || '10' // Default to 10km radius
  });
  const [showTooltip, setShowTooltip] = useState(false);
  
  const [stateCode, setStateCode] = useState(null);
  const [cities, setCities] = useState([]);
  const states = State.getStatesOfCountry("IN");

  // Update cities when state changes
  useEffect(() => {
    if (filters.state) {
      const selectedState = states.find(s => s.name === filters.state);
      if (selectedState) {
        setStateCode(selectedState.isoCode);
        setCities(City.getCitiesOfState("IN", selectedState.isoCode));
      }
    } else {
      setCities([]);
      setStateCode(null);
    }
  }, [filters.state]);

  const handleStateChange = (e) => {
    const stateName = e.target.value;
    setFilters(prev => ({
      ...prev,
      state: stateName,
      district: '' // Reset district when state changes
    }));
  };

  const handleDistrictChange = (e) => {
    setFilters(prev => ({
      ...prev,
      district: e.target.value
    }));
  };

  const handleRadiusChange = (e) => {
    const value = e.target.value;
    console.log("Radius changed to:", value);
    setFilters(prev => ({
      ...prev,
      radius: value
    }));
  };

  const handleApplyFilter = () => {
    if (onFilterChange) {
      // Remove empty filters but keep radius if it exists
      const filtersToApply = {...filters};
      Object.keys(filtersToApply).forEach((key) => {
        if (filtersToApply[key] === "" && key !== 'radius') {
          delete filtersToApply[key];
        }
      });
      
      // Ensure radius is always a string
      if (filtersToApply.radius) {
        filtersToApply.radius = filtersToApply.radius.toString();
      }
      
      console.log("Applying filters:", filtersToApply);
      onFilterChange(filtersToApply);
    }
  };

  const handleClearFilter = () => {
    const clearedFilters = {
      state: '',
      district: '',
      radius: ''
    };
    setFilters(clearedFilters);
    if (onFilterChange) {
      onFilterChange({});
    }
  };

  return (
    <Box 
      p={4} 
      bg="white" 
      borderRadius="md" 
      shadow="md"
      mb={4}
    >
      <Text fontWeight="bold" mb={3}>Filter Products by Location</Text>
      <Flex direction={{ base: "column", md: "row" }} gap={3}>
        <Select 
          placeholder="Select State" 
          value={filters.state}
          onChange={handleStateChange}
          flex={1}
        >
          {states.map(state => (
            <option key={state.isoCode} value={state.name}>
              {state.name}
            </option>
          ))}
        </Select>
        
        <Select 
          placeholder="Select District" 
          value={filters.district}
          onChange={handleDistrictChange}
          isDisabled={!filters.state}
          flex={1}
        >
          {cities.map(city => (
            <option key={city.name} value={city.name}>
              {city.name}
            </option>
          ))}
        </Select>
        
        {showDistanceFilter ? (
          <Box flex={1}>
            <Slider
              id="distance-slider"
              value={parseInt(filters.radius) || 10}
              min={1}
              max={100}
              step={1}
              onChange={(val) => {
                console.log("Slider value changed to:", val);
                setFilters(prev => ({ ...prev, radius: val.toString() }));
              }}
              onMouseEnter={() => setShowTooltip(true)}
              onMouseLeave={() => setShowTooltip(false)}
            >
              <SliderTrack bg="green.100">
                <SliderFilledTrack bg="green.500" />
              </SliderTrack>
              <Tooltip
                hasArrow
                bg="green.500"
                color="white"
                placement="top"
                isOpen={showTooltip}
                label={`${filters.radius || 10} km`}
              >
                <SliderThumb boxSize={6}>
                  <Box color="green.500" as={FaMapMarkerAlt} />
                </SliderThumb>
              </Tooltip>
            </Slider>
            <Text fontSize="xs" textAlign="center" mt={1}>{filters.radius || 10} km radius</Text>
          </Box>
        ) : (
          <Select 
            placeholder="Distance Radius" 
            value={filters.radius}
            onChange={handleRadiusChange}
            flex={1}
          >
            <option value="5">Within 5 km</option>
            <option value="10">Within 10 km</option>
            <option value="20">Within 20 km</option>
            <option value="50">Within 50 km</option>
            <option value="100">Within 100 km</option>
          </Select>
        )}
      </Flex>
      
      <Divider my={3} />
      
      <HStack spacing={4} justifyContent="flex-end">
        <Button 
          size="sm" 
          variant="outline"
          onClick={handleClearFilter}
        >
          Clear Filters
        </Button>
        <Button 
          size="sm" 
          colorScheme="green"
          onClick={handleApplyFilter}
        >
          Apply Filters
        </Button>
      </HStack>
    </Box>
  );
};

export default FilterBar;